import { Injectable } from '@angular/core';
import { PessoaJuridicaChanelProvider } from 'src/app/shared/models/pessoa-juridica/pessoa-juridica-chanel-provider.model';
import { LoggerService } from '../../core/services/logger.service';
import { PJ_CONSTANTS } from 'src/app/shared/constants/pessoa-juridica.constants';
import { LocalStorageService } from '../services/local-storage.service';
import { SessionDataDTO } from '../CXOne/DataTransferObjects/SessionDatDTO';
import {
  CIFDatadogService,
  CloseTicketByEventProps,
} from 'src/app/shared/services/cif-datadog';
import { CriacaoTicketPessoaJuridica } from 'src/app/shared/models/pessoa-juridica/pessoa-juridica-criacao-ticket.model';
import { MediaType } from 'src/app/utils/media-type';
import { JsonConverterUtils } from '../CXOne/util/JsonConverterUtils';
import { LocalStorageUtils } from '../CXOne/util/LocalStorageUtils';
export type Ticket = { id_interacao: string | null; id_ticket: string | null };

@Injectable({
  providedIn: 'root',
})
export class DynamicsPessoaJuridica {
  service: string = 'DYNAMICS PJ';
  private messageHandler: (event: Event) => void;
  constructor(
    private logger: LoggerService,
    private localStorage: LocalStorageService,
    private cifDatadogService: CIFDatadogService,
    private localStorageUtils: LocalStorageUtils,
    private jsonConverterUtils: JsonConverterUtils,
  ) {
    this.messageHandler = this.DynamicsMessageHandler.bind(this);
    window.addEventListener('message', this.messageHandler);
  }

  initialize(cifUrl: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.logger.info(`${this.service} - inicializando CIF: ${cifUrl}`);
      const script = this.createScriptElement(cifUrl);
      document.body.appendChild(script);
      const onCIFInitDone = async (event: Event) => {
        try {
          await this.configureCIF();
          this.logger.info(`${this.service} - CIF inicializado com sucesso`);
          resolve();
        } catch (error) {
          if (error instanceof Error && error.message) {
            reject(error);
          } else {
            this.logger.error(
              `${this.service} - Erro ao criar sessão: `,
              error,
            );
            reject(
              new Error(
                `${this.service} - Erro ao criar sessão: ${error instanceof Error ? error.message : error}`,
              ),
            );
          }
        } finally {
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };
      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  private createScriptElement(cifUrl: string): HTMLScriptElement {
    const script = document.createElement('script');
    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');
    return script;
  }

  private async configureCIF(): Promise<void> {
    try {
      await window.Microsoft.CIFramework.setMode(1);
      await window.Microsoft.CIFramework.addHandler(
        'onmodechanged',
        async (eventData: any) => {
          await window.Microsoft.CIFramework.setMode(1);
          return Promise.resolve();
        },
      );
      /*await window.Microsoft.CIFramework.setClickToAct(true);
      await window.Microsoft.CIFramework.addHandler(
        'onSessionClosed',
        this.cifCloseSession(),
      );*/
      await (window as any).Microsoft.CIFramework.setWidth(400);
    } catch (error) {
      this.logger.error(`${this.service} - Erro ao configurar CIF: `, error);
      throw new Error(`${this.service} - Erro ao configurar CIF: ${error}`);
    }
  }

  public async cifCreateTicket(
    tableName: string,
    attachData: CriacaoTicketPessoaJuridica,
    retrieveRecordTable: string,
    retrieveRecordCol: string,
    RECALL_LIMIT: number,
    RECALL_DELAY_MS: number,
    contactId: string,
    mediaType: MediaType,
  ): Promise<Ticket> {
    let retorno: Ticket = {
      id_interacao: null,
      id_ticket: null,
    };

    if (typeof window.Microsoft !== 'undefined') {
      this.cifDatadogService.openTicket.request.create({
        attachData: attachData,
        masterId: contactId,
        mediaType,
      });

      try {
        const result = await window.Microsoft.CIFramework.createRecord(
          tableName,
          this.jsonConverterUtils.JsonStringfy(attachData),
        );

        this.cifDatadogService.openTicket.request.success({
          attachData,
          mediaType,
          masterId: contactId,
        });

        this.logger.info(`${this.service} - ID Interação criado: ${result}`);
        retorno.id_interacao = this.jsonConverterUtils.JsonParsed(result).id;

        //recuperacao do ID do ticket
        if (retorno.id_ticket == null && retorno.id_interacao != null) {
          retorno.id_ticket = await this.cifRetrieveRecord(
            retorno.id_interacao,
            tableName,
            retrieveRecordTable,
            retrieveRecordCol,
            RECALL_LIMIT,
            RECALL_DELAY_MS,
            mediaType,
            contactId,
          );
          this.logger.info(
            `${this.service} - Ticket recuperado: ${retorno.id_ticket}`,
          );
        }
      } catch (error) {
        this.cifDatadogService.openTicket.request.fail({
          attachData,
          mediaType,
          masterId: contactId,
          error,
        });

        if (error instanceof Error && error.message) {
          throw error;
        } else {
          this.logger.error(
            `${this.service} - Erro ao criar interação ou recuperar ticket: `,
            error,
          );
          throw new Error(
            `${this.service} - Erro ao criar interação ou recuperar ticket: ${error instanceof Error ? error.message : error}`,
          );
        }
      }
      return retorno;
    } else {
      this.logger.error(`${this.service} - Microsoft.CIFramework nao definido`);
      throw new Error(`${this.service} - Microsoft.CIFramework nao definido`);
    }
  }

  //verificar se o ticket já foi criado no Dynamics
  private async cifRetrieveRecord(
    interactionId: string,
    tableName: string,
    retrieveRecordTable: string,
    retrieveRecordCol: string,
    RECALL_LIMIT: number,
    RECALL_DELAY_MS: number,
    mediaType: MediaType,
    masterId: string,
  ): Promise<string | null> {
    let idTicket = null;

    this.cifDatadogService.openTicket.retrieve.start({
      interactionId,
      mediaType,
      masterId,
    });

    for (let attempt = 0; attempt < RECALL_LIMIT; attempt++) {
      await this.delay(RECALL_DELAY_MS);

      const options = `?$select=itau_protocolo_interacao&$expand=${retrieveRecordTable}($select=${retrieveRecordCol})`;
      try {
        const result = await (
          window as any
        ).Microsoft.CIFramework.retrieveRecord(
          tableName,
          interactionId,
          options,
        );
        const interaction = this.jsonConverterUtils.JsonParsed(result);
        if (interaction[retrieveRecordTable]?.[0] != null) {
          idTicket = interaction[retrieveRecordTable][0][retrieveRecordCol];

          this.cifDatadogService.openTicket.retrieve.success({
            interactionId,
            mediaType,
            masterId,
          });
          break;
        }
      } catch (error) {
        this.cifDatadogService.openTicket.retrieve.fail({
          interactionId,
          mediaType,
          masterId,
        });
        this.logger.error(
          `${this.service} - Erro ao recuperar ticket na tentativa ${attempt + 1}: `,
          error,
        );
        throw new Error(
          `${this.service} - Erro ao recuperar ticket na tentativa ${attempt + 1}: ${error instanceof Error ? error.message : error}`,
        );
      }
    }
    return idTicket;
  }

  //Este método suporta apenas modelos de sessão do tipo Genérico e um máximo de 10 sessões simultaneamente.
  public async cifCreateSession(
    inputTemplate: any,
    mediaType: MediaType | null,
    masterId: string,
  ): Promise<string> {
    let interactionId = null;
    try {
      interactionId = inputTemplate['templateParameters'].id_interacao;
      this.cifDatadogService.openTicket.session.request({
        interactionId,
        mediaType,
        masterId,
        template: inputTemplate,
      });

      const canCreate = await this.canCreateSession();
      if (canCreate) {
        const sessionId = await (
          window as any
        ).Microsoft.CIFramework.createSession(inputTemplate);

        this.cifDatadogService.openTicket.session.success({
          sessionId: sessionId,
          interactionId: interactionId,
          mediaType,
          masterId,
        });

        return sessionId;
      } else {
        this.logger.error(
          `${this.service} - Não é possível criar uma sessão com o template fornecido: ${this.jsonConverterUtils.JsonStringfy(inputTemplate)}`,
        );
        throw new Error(
          `${this.service} - Não é possível criar uma sessão com o template fornecido: ${this.jsonConverterUtils.JsonStringfy(inputTemplate)}`,
        );
      }
    } catch (error) {
      this.cifDatadogService.openTicket.session.fail({
        interactionId,
        template: inputTemplate,
        mediaType,
        masterId,
        error,
      });

      if (error instanceof Error && error.message) {
        throw error;
      } else {
        this.logger.error(`${this.service} - Erro ao criar sessão: `, error);
        throw new Error(
          `${this.service} - Erro ao criar sessão: ${error instanceof Error ? error.message : error}`,
        );
      }
    }
  }

  private async canCreateSession(): Promise<boolean> {
    try {
      return await window.Microsoft.CIFramework.canCreateSession();
    } catch (error) {
      this.logger.error(
        `${this.service} - Erro ao verificar criação de sessão: `,
        error,
      );
      throw new Error(
        `${this.service} - Erro ao verificar criação de sessão: ${error instanceof Error ? error.message : error}`,
      );
    }
  }

  //event da nice ao receber um disconect
  public async cifCloseSession(
    sessionId: string,
    contactId: string,
    interactionId: string,
    mediaType: MediaType,
  ): Promise<void> {
    const closeEventLogDetails: CloseTicketByEventProps = {
      sessionId: sessionId,
      interactionId: interactionId,
      masterId: contactId,
      mediaType,
    };

    try {
      this.cifDatadogService.closeTicket.start(
        'fecharSessao',
        closeEventLogDetails,
      );

      await this.raiseCloseSessionEvent(sessionId, interactionId);

      this.cifDatadogService.closeTicket.success(
        'fecharSessao',
        closeEventLogDetails,
      );
    } catch (error) {
      this.cifDatadogService.closeTicket.fail('fecharSessao', {
        sessionId: sessionId,
        interactionId: interactionId,
        error,
        masterId: contactId,
        mediaType,
      });
      if (error instanceof Error && error.message) {
        throw error;
      } else {
        this.logger.error(
          `${this.service} - Erro ao fechar sessao(${sessionId}): `,
          error,
        );
        throw new Error(
          `${this.service} - Erro ao fechar sessao(${sessionId}) - ${error instanceof Error ? error.message : error}`,
        );
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  //pega os eventos ao alternar a aba de atendimento no Dynamics
  public async changeTabDynamics(contactId: string) {
    await window.Microsoft.CIFramework.addHandler(
      'onSessionSwitched',
      async (eventData: string) => {
        const eventJson = this.jsonConverterUtils.JsonParsed(eventData);
        const sessionId = eventJson.sessionId;
        const focused = eventJson.focused;
        if (sessionId !== 'session-id-1' && contactId !== '') {
          //this.logger.info('PJ (onSessionSwitched) - trocando aba de atendimento na nice.');
          if (focused) {
            await window.Microsoft.CIFramework.requestFocusSession(sessionId);
            //CODIGO PARA FOCAR NO ATENDIMENTO DA NICE
          } else {
            //this.removeFocusNice(focused, contactId, sessionId);
            //CODIGO PARA REMOVER O ATENDIMENTO DA NICE
          }
        }
      },
    );
  }

  //pega os eventos ao fechar a aba de atendimento no Dynamics
  public async closeTabDynamics(contactId: string) {
    await window.Microsoft.CIFramework.addHandler(
      'onSessionClosed',
      (eventData: string) => {
        const eventJson = this.jsonConverterUtils.JsonParsed(eventData);
        const sessionId = eventJson.sessionId;
        const focused = eventJson.focused;
        this.logger.info(
          `${this.service} - sessao fechada no dynamics:`,
          eventJson,
        );
      },
    );
  }

  public async loadTemplateValues(): Promise<PessoaJuridicaChanelProvider | null> {
    try {
      const channelProviderVariablesRaw = await (
        window as any
      ).Microsoft.CIFramework.getEnvironment();
      const channelProviderVariables = this.jsonConverterUtils.JsonParsed(
        channelProviderVariablesRaw,
      );

      let customParams: any = {};
      if (channelProviderVariables.msdyn_customparams) {
        customParams = Object.fromEntries(
          channelProviderVariables.msdyn_customparams
            .split('&')
            .map((kv: string) => kv.split('=')),
        );
      }

      const templateValues: PessoaJuridicaChanelProvider = {
        TEMPLATE_CREATE_SESSION: customParams.template_create_session,
        TEMPLATE_SESSAO_PADRAO: customParams.template_sessao_padrao,
        RETRIEVE_RECORD_TABLE: customParams.retrieve_record_table,
        RETRIEVE_RECORD_COL: customParams.retrieve_record_col,
        TABLE_NAME: customParams.table_name,
        ITAU_NAME: customParams.itau_name,
        ITAU_U_LK: customParams.itau_u_lk,
        ITAU_U_NOMEPROJETO: customParams.itau_u_nomeprojeto,
        ATTACHDATA_U_JSON: customParams.attachdata_u_json,
      };

      this.logger.info(
        `${this.service} - Variaveis channel provider: ${this.jsonConverterUtils.JsonStringfy(templateValues)}`,
      );
      return templateValues;
    } catch (error) {
      this.logger.error(
        `${this.service} - Erro ao carregar template do chanel provider:`,
        error,
      );
      return null;
    }
  }

  public async openTicket(
    AttachData: any,
    contactId: string,
    isFocused: boolean,
    mediaType: MediaType,
  ): Promise<string> {
    this.cifDatadogService.openTicket.request.create({
      attachData: AttachData,
      masterId: contactId,
      mediaType,
    });

    try {
      let retorno: Ticket = {
        id_interacao: null,
        id_ticket: null,
      };

      let attachJson = this.jsonConverterUtils.JsonStringfy(AttachData.U_JSON);

      attachJson = this.jsonConverterUtils.JsonParsed(attachJson);

      const templateValues = await this.loadTemplateValues();

      const tableName = templateValues?.TABLE_NAME;

      const datacb: any = {
        itau_name: templateValues?.ITAU_NAME,
        itau_mst_attachdata: attachJson,
        'itau_u_lk_projeto@odata.bind': templateValues?.ITAU_U_LK,
        itau_u_nomeprojeto: templateValues?.ITAU_U_NOMEPROJETO,
        itau_st_id_conexao: contactId,
      };

      this.logger.info(`${this.service} - Dados do ticket: `, datacb);

      const result = await window.Microsoft.CIFramework.createRecord(
        templateValues?.TABLE_NAME,
        this.jsonConverterUtils.JsonStringfy(datacb),
      );
      this.logger.info(`${this.service} - ID Interação criado: ${result}`);
      retorno.id_interacao = this.jsonConverterUtils.JsonParsed(result).id;

      //recuperacao do ID do ticket
      if (retorno.id_ticket == null && retorno.id_interacao != null) {
        retorno.id_ticket = await this.cifRetrieveRecord(
          retorno.id_interacao,
          tableName ?? '',
          templateValues?.RETRIEVE_RECORD_TABLE ?? '',
          templateValues?.RETRIEVE_RECORD_COL ?? '',
          PJ_CONSTANTS.RECALL_LIMIT,
          PJ_CONSTANTS.RECALL_DELAY_MS,
          mediaType,
          contactId,
        );
        this.logger.info(
          `${this.service} - Ticket recuperado: ${retorno.id_ticket}`,
        );

        const templateAtendimento = {
          templateName: templateValues?.TEMPLATE_CREATE_SESSION,
          templateParameters: {
            id_interacao: retorno.id_interacao,
            id_ticket: retorno.id_ticket,
            oq2_cliente_on: true,
          },
          isFocused: isFocused,
        };
        const sessionId = await this.cifCreateSession(
          templateAtendimento,
          mediaType,
          contactId,
        );
        this.logger.info(`PJ - Sessão criada ${contactId} = ${sessionId}`);

        const sessionData: SessionDataDTO = {
          contactCenterInteractionId: contactId,
          dynamicsSessionId: sessionId,
          dynamicsinteractionId: retorno.id_interacao,
        };

        this.addSessionRelationship(sessionData);

        this.cifDatadogService.openTicket.request.success({
          attachData: AttachData,
          mediaType,
          masterId: contactId,
        });

        return sessionId;
      }

      return '';
    } catch (error) {
      this.cifDatadogService.openTicket.request.fail({
        error,
        attachData: AttachData,
        mediaType,
        masterId: contactId,
      });
      if (error instanceof Error && error.message) {
        throw error;
      } else {
        this.logger.error(
          `${this.service} - Erro ao criar interação ou recuperar ticket: `,
          error,
        );
        throw new Error(
          `${this.service} - Erro ao criar interação ou recuperar ticket: ${error instanceof Error ? error.message : error}`,
        );
      }
    }
  }

  public async endTicket(contactId: string, sessionId: string): Promise<void> {
    try {
      this.logger.info('PJ - Contato desconectado');
      const sessaoAtiva = this.localStorage.getSessaoAtendimento({
        contactId,
        sessionId,
      });

      const sessionRelationship = this.getSessionRelationship(contactId);

      if (!sessaoAtiva.sessionId) {
        this.logger.warn(
          'PJ - sessionId não encontrado no localStorage, não é possível encerrar',
          contactId,
        );
        return;
      }

      this.localStorage.deleteSessaoAtendimento({ contactId, sessionId });
      this.removeSessionRelationship(contactId);

      await this.raiseCloseSessionEvent(
        sessionRelationship?.dynamicsSessionId,
        sessionRelationship?.dynamicsinteractionId,
      );

      this.logger.info(`PJ - Sessão encerrada cif:`, sessaoAtiva);
    } catch (error) {
      this.logger.error('PJ - Erro ao encerrar sessão: ', error);
      throw error;
    }
  }

  private async raiseCloseSessionEvent(
    sessionId?: string,
    correlationId?: string,
  ) {
    const context = { oq2_cliente_on: false };

    await window.Microsoft.CIFramework.updateContext(context, sessionId);

    const closeArgs = {
      sessionId,
      correlationId,
    };

    await window.Microsoft.CIFramework.raiseEvent('fecharSessao', closeArgs);
  }

  public DynamicsMessageHandler(e: any) {
    if (e.data.messageType === 'onSessionSwitched') {
      this.OnSessionSwitchHandlerFromWindow(e);
    }
  }
  public async OnSessionSwitchHandlerFromWindow(e: any) {
    this.logger.info(
      'Troca de sessão solicitada no dynamics via message',
      this.jsonConverterUtils.JsonStringfy(e),
    );

    if (e.data.messageData.focused === true) {
      return;
    }

    const ContactCenterInteractionId =
      this.localStorageUtils.safeGetItem('interactionFocused') ?? '';
    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );

    let controleFocarSessao = {
      sessionId: sessionRelationship?.dynamicsSessionId,
      correlationId: sessionRelationship?.dynamicsinteractionId,
    };

    this.logger.info(
      'Solicitacao retorno a sessão do dynamics.',
      this.jsonConverterUtils.JsonStringfy(controleFocarSessao),
    );
    await window.Microsoft.CIFramework.raiseEvent(
      'ControleFocarSessao',
      controleFocarSessao,
    );
  }

  public async OnSessionClosedHandlerFromWindow(e: any) {
    this.logger.info('Encerramento de sessão solicitada no dynamics.', e);
  }

  public async ChangeSessionFocus(
    ContactCenterInteractionId: string,
  ): Promise<void> {
    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );

    await window.Microsoft.CIFramework.raiseEvent('ControleFocarSessao', {
      sessionId: sessionRelationship?.dynamicsSessionId,
      correlationId: sessionRelationship?.dynamicsinteractionId,
    });
  }

  private addSessionRelationship(sessionData: SessionDataDTO): void {
    this.localStorageUtils.safeSetItem(
      `sessionRelationship-${sessionData.contactCenterInteractionId}`,
      this.jsonConverterUtils.JsonStringfy(sessionData),
    );
  }

  private getSessionRelationship(
    ContactCenterInteractionId: string,
  ): SessionDataDTO | null {
    const value = this.localStorageUtils.safeGetItem(
      `sessionRelationship-${ContactCenterInteractionId}`,
    );
    if (value) {
      return this.jsonConverterUtils.JsonParsed(value);
    }
    return null;
  }

  private removeSessionRelationship(contactCenterInteractionId: string): void {
    this.localStorageUtils.safeRemoveItem(
      `sessionRelationship-${contactCenterInteractionId}`,
    );
  }
}
